import { Skeleton } from "@/components/ui/skeleton";
import { useActiveMeeting } from "@/hooks/useMeetings";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import heroLogoFallback from "@/assets/hero-logo.png";
import RegistrationForm from "@/components/public/RegistrationForm";
import { BRAND } from "@/brand.config";

/**
 * Public landing page. Single fixed-viewport view — logo, value prop,
 * optional event details, signup form. Reads naturally with or without
 * an event scheduled.
 */

/**
 * Format a meeting's datetime in the viewer's local timezone, including
 * day of week and timezone abbreviation. Removes ambiguity for visitors
 * not in the same timezone as the admin who scheduled it.
 *
 * Example: "Thursday, May 6 at 7:00 PM CDT"
 */
function formatEventDate(date: Date): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
  }).formatToParts(date);

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";

  const weekday = get("weekday");
  const month = get("month");
  const day = get("day");
  const hour = get("hour");
  const minute = get("minute");
  const dayPeriod = get("dayPeriod");
  const tz = get("timeZoneName");

  return `${weekday}, ${month} ${day} at ${hour}:${minute} ${dayPeriod}${tz ? ` ${tz}` : ""}`;
}

const Index = () => {
  const { data: activeMeeting, isLoading: isLoadingMeeting } = useActiveMeeting();
  const { data: settings } = useSiteSettings();

  const heroLogo = settings?.hero_logo_url || heroLogoFallback;
  const ctaText = settings?.cta_button_text || BRAND.ctaButtonText;
  const successHeading = settings?.success_heading || BRAND.successHeading;
  const successSubtext = settings?.success_subtext || BRAND.successSubtext;
  const valueProp = settings?.value_prop || BRAND.valueProp;

  const eventDate = activeMeeting?.meeting_datetime
    ? formatEventDate(new Date(activeMeeting.meeting_datetime))
    : null;
  const eventTopic = activeMeeting?.topic ?? null;

  return (
    <main
      className="texture-overlay bg-background flex flex-col items-center justify-center overflow-hidden"
      style={{ height: "100dvh", minHeight: "100vh" }}
    >
      <div className="w-full max-w-md px-5 py-6 sm:py-10 flex flex-col items-center">
        <img
          src={heroLogo}
          alt="Logo"
          loading="eager"
          fetchPriority="high"
          className="animate-reveal-blur w-full max-w-[320px] max-h-[26vh] sm:max-h-[30vh] object-contain mb-4 sm:mb-6"
        />

        {/* Value prop — one-line "what is this" for first-time visitors.
            When an event is scheduled, the date renders inline beneath
            it as the focal action. No "NEXT EVENT" label or "Sign up
            below" prompt — the form button carries the CTA. */}
        <p className="animate-reveal-blur-delay-1 text-center text-base sm:text-lg font-serif text-foreground max-w-xs sm:max-w-sm">
          {valueProp}
        </p>

        {isLoadingMeeting ? (
          <div className="animate-reveal-blur-delay-1 text-center mt-4 mb-6 sm:mb-8 w-full space-y-2">
            <Skeleton className="h-6 w-56 mx-auto" />
            <Skeleton className="h-4 w-44 mx-auto" />
          </div>
        ) : eventDate ? (
          <div className="animate-reveal-blur-delay-1 text-center mt-3 sm:mt-4 mb-6 sm:mb-8 w-full">
            <p className="font-serif text-base sm:text-lg font-bold tracking-wide text-primary">
              {eventDate}
            </p>
            {eventTopic && (
              <p className="mt-1.5 font-serif italic text-sm sm:text-base text-muted-foreground">
                {eventTopic}
              </p>
            )}
          </div>
        ) : (
          <div className="mb-6 sm:mb-8" />
        )}

        <div className="w-full">
          <RegistrationForm
            ctaButtonText={ctaText}
            successHeading={successHeading}
            successSubtext={successSubtext}
            activeMeetingId={activeMeeting?.id}
          />
        </div>
      </div>
    </main>
  );
};

export default Index;
